<template>
    <div class="physical-people-page">
        <a-spin tip="Carregando..." :spinning="spinning">
            <a-config-provider :locale="currentLocale">
                <a-card>
                    <template #title>
                        <a-breadcrumb separator=">">
                            <a-breadcrumb-item><span>Pessoas Físicas</span></a-breadcrumb-item>
                            <a-breadcrumb-item>Listar</a-breadcrumb-item>
                        </a-breadcrumb>
                    </template>
                    <template #extra>
                        <a-button :icon="h(PlusOutlined)" @click="openModal()">Novo Registro</a-button>
                    </template>

                    <div class="table-toolbar">
                        <a-input-search
                            v-model:value="search"
                            placeholder="Pesquisar por nome ou CPF"
                            allow-clear
                            :enter-button="false"
                            style="max-width: 320px"
                            @search="load"
                        />
                    </div>

                    <a-table
                        :columns="columns"
                        :data-source="dataSource"
                        :row-key="(r) => r.id"
                        bordered
                    >
                        <template #bodyCell="{ column, record }">
                            <template v-if="column.key === 'acao'">
                                <a-tooltip title="Alterar">
                                    <a-button type="primary" class="center-flex" shape="circle" @click="openModal(record)">
                                        <template #icon><FormOutlined /></template>
                                    </a-button>
                                </a-tooltip>
                                <a-popconfirm
                                    :title="'Excluir \'' + (record.name || '') + '\'?'"
                                    ok-text="Excluir"
                                    cancel-text="Cancelar"
                                    :ok-button-props="{ danger: true }"
                                    @confirm="destroy(record)"
                                >
                                    <template #icon><QuestionCircleOutlined style="color: #ff4d4f" /></template>
                                    <a-tooltip title="Excluir">
                                        <a-button type="primary" class="center-flex" danger shape="circle">
                                            <template #icon><DeleteOutlined /></template>
                                        </a-button>
                                    </a-tooltip>
                                </a-popconfirm>
                            </template>
                        </template>
                    </a-table>
                </a-card>
                <a-modal
                    v-model:open="modalVisible"
                    :title="editingId ? 'Editar Pessoa Física' : 'Nova Pessoa Física'"
                    :mask-closable="false"
                    width="520px"
                    :z-index="10000"
                    @ok="submitForm"
                    @cancel="closeModal"
                >
                    <a-form layout="vertical" :model="form">
                        <a-form-item label="Nome" required>
                            <a-input v-model:value="form.name" placeholder="Nome completo" />
                        </a-form-item>
                        <a-form-item label="CPF" required>
                            <a-input v-model:value="form.cpf" placeholder="Somente números (11 dígitos)" :maxlength="11" />
                        </a-form-item>
                        <a-form-item label="RG">
                            <a-input v-model:value="form.rg" placeholder="RG (opcional)" />
                        </a-form-item>
                        <a-form-item label="Data de nascimento">
                            <a-date-picker
                                v-model:value="form.birth_date"
                                :locale="datePickerLocale"
                                format="DD/MM/YYYY"
                                value-format="YYYY-MM-DD"
                                style="width: 100%"
                                placeholder="Selecione a data"
                                :get-popup-container="(node) => node.closest('.ant-modal-content') || document.body"
                            />
                        </a-form-item>
                        <a-form-item label="E-mail">
                            <a-input v-model:value="form.email" placeholder="E-mail (opcional)" />
                        </a-form-item>
                        <a-form-item label="Telefone">
                            <a-input v-model:value="form.phone" placeholder="Telefone (opcional)" />
                        </a-form-item>
                        <a-form-item label="Ativo">
                            <a-switch v-model:checked="form.active" />
                        </a-form-item>
                    </a-form>
                </a-modal>
            </a-config-provider>
        </a-spin>
    </div>
</template>

<script setup>
import { ref, h, onMounted } from 'vue';
import { PlusOutlined, FormOutlined, DeleteOutlined, QuestionCircleOutlined } from '@ant-design/icons-vue';
import ptBR from 'ant-design-vue/es/locale/pt_BR';
import datePickerPtBR from 'ant-design-vue/es/date-picker/locale/pt_BR';
import { message } from 'ant-design-vue';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';

const route = '/api/register/physical-person';
const currentLocale = ref(ptBR);
const datePickerLocale = datePickerPtBR;
dayjs.locale('pt-br');
const spinning = ref(false);
const search = ref('');
const dataSource = ref([]);
const modalVisible = ref(false);
const editingId = ref(null);

const form = ref({
    name: '',
    cpf: '',
    rg: '',
    birth_date: null,
    email: '',
    phone: '',
    active: true,
});

const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 70 },
    { title: 'Nome', dataIndex: 'name', key: 'name', ellipsis: true },
    { title: 'CPF', dataIndex: 'cpf', key: 'cpf', width: 120 },
    { title: 'RG', dataIndex: 'rg', key: 'rg', width: 100 },
    { title: 'E-mail', dataIndex: 'email', key: 'email', ellipsis: true },
    { title: 'Telefone', dataIndex: 'phone', key: 'phone', width: 120 },
    { title: 'Ação', key: 'acao', width: 120 },
];

function openModal(record = null) {
    editingId.value = record ? record.id : null;
    form.value = {
        name: record ? record.name ?? '' : '',
        cpf: record ? (record.cpf ?? '') : '',
        rg: record ? (record.rg ?? '') : '',
        birth_date: record?.birth_date ?? null,
        email: record ? (record.email ?? '') : '',
        phone: record ? (record.phone ?? '') : '',
        active: record ? !!record.active : true,
    };
    modalVisible.value = true;
}

function closeModal() {
    modalVisible.value = false;
    editingId.value = null;
    form.value = { name: '', cpf: '', rg: '', birth_date: null, email: '', phone: '', active: true };
}

async function load() {
    try {
        spinning.value = true;
        const params = search.value?.trim() ? { search: search.value.trim() } : {};
        const { data } = await window.axios.get(route, { params });
        dataSource.value = data?.data ?? [];
    } catch (err) {
        message.error(err.response?.data?.message || 'Erro ao carregar.');
    } finally {
        spinning.value = false;
    }
}

async function submitForm() {
    const cpf = String(form.value.cpf || '').replace(/\D/g, '');
    if (!form.value.name?.trim()) {
        message.warning('Informe o nome.');
        return;
    }
    if (cpf.length !== 11) {
        message.warning('CPF deve ter 11 dígitos.');
        return;
    }
    try {
        spinning.value = true;
        const payload = {
            name: form.value.name.trim(),
            cpf,
            rg: form.value.rg?.trim() || null,
            birth_date: form.value.birth_date || null,
            email: form.value.email?.trim() || null,
            phone: form.value.phone?.trim() || null,
            active: !!form.value.active,
        };
        if (editingId.value) {
            await window.axios.put(`${route}/${editingId.value}`, payload);
            message.success('Alterado com sucesso!');
        } else {
            await window.axios.post(route, payload);
            message.success('Criado com sucesso!');
        }
        closeModal();
        await load();
    } catch (err) {
        const msg = err.response?.data?.message || err.response?.data?.errors?.cpf?.[0] || 'Erro ao salvar.';
        message.error(msg);
    } finally {
        spinning.value = false;
    }
}

async function destroy(record) {
    try {
        spinning.value = true;
        await window.axios.delete(`${route}/${record.id}`);
        message.success('Excluído com sucesso!');
        await load();
    } catch (err) {
        message.error(err.response?.data?.message || 'Erro ao excluir.');
    } finally {
        spinning.value = false;
    }
}

onMounted(() => load());
</script>

<style scoped>
.physical-people-page {
    padding: 24px;
}
.table-toolbar {
    margin-bottom: 16px;
}
.center-flex {
    display: inline-flex;
    align-items: center;
    justify-content: center;
}
</style>
